import React, { useEffect, useState } from 'react';
import Box from '@tds/core-box';
import Radio from '@tds/core-radio';
import axios from 'axios';
import Heading from '@tds/core-heading';
import Button from '@tds/core-button';
import Spinner from '@tds/core-spinner';
import Text from '@tds/core-text';
import { ExpandCollapse } from '@tds/core-expand-collapse';
import { useLocaleContext } from '../context/LocaleContext';
import {
  contentfulPagesConfig,
  getContentfulData,
  getMultipleData
} from '../contentful';
import EN from '../../EN.json';
import FR from '../../FR.json';
import ValidateLoader from '../UI/validateLoader';
import { pagesNames } from '../utils/constant';
import { addLocale } from '../misc';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { Error } from '../design/elements';
import { getEnvironment } from '../utils/request';
import { styleInputField, styleTextBold, Spacer } from './styles';
import DefectManagement from '../defectManagement';
import { FormStepContainerBRP } from '../form/css';
import { scrollToErrors } from '../utils/utils';

const { name: envName } = getEnvironment();

const InputTypes = {
  RADIO: 'Radio',
  TEXT: 'Text',
  MULTILINE_TEXT: 'MultiLineText',
  FILE: 'File'
};

const ReportAnIssue = ({ history }) => {
  const locale = useLocaleContext();
  const addLocaleToUrl = addLocale(locale);
  const [page, setPage] = useState(null);
  const [loader, setLoader] = useState(true);
  const [isSubmitting, setSubmitting] = useState(false);
  const [fileUploadError, setFileUploadError] = useState('');
  const {
    REPORT_ISSUE_PAGE: { API_ERROR },
    MESSAGES: { ERROR }
  } = useLocalizedStringsContext();
  const {
    headerLinks: {
      defectManagment,
      reportAnIssue: reportIssueTitle,
      Max_200_characters,
      Max_500_characters,
      Button_submit
    },
    DEFECT_MANAGEMENT: { STATUS_OPTIONS } = {}
  } = locale === 'en-US' ? EN : FR;
  const [errors, setErrors] = useState({});
  const config = { ...contentfulPagesConfig, locale };

  useEffect(() => {
    getContentfulData(config).then(res => {
      const result = getMultipleData(res);
      const { fields: howToVideosPage } = result.find(
        r => r.fields.name === pagesNames.REPORT_AN_ISSUE
      );
      setPage(howToVideosPage);
      setLoader(false);
    });
  }, [locale]);

  const renderRadio = ({ fields: { label, options, name } }) => {
    return (
      <>
        <br />
        <Box tag="fieldset" between={2}>
          <legend style={{ marginBottom: '10px' }}>
            <Text bold size="medium">
              {label}
            </Text>
          </legend>
          <div
            style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '-1%' }}
          >
            {options.map(({ fields: { label, value } }) => (
              <Radio label={label} name={name} value={value} />
            ))}
          </div>

          {errors[name] && errors[name] && (
            <>
              <Spacer />
              <Error text={errors[name]} />
            </>
          )}
        </Box>
      </>
    );
  };

  const renderText = ({ fields: { label, name, placeholder } }) => {
    return (
      <Box tag="fieldset" between={5}>
        <br />
        <label style={{ marginBottom: 10, fontWeight: 'bold' }}>{label}</label>
        <input
          name={name}
          placeholder={placeholder}
          maxlength="200"
          className="frg-input-field color-light"
          style={{ width: '100%' }}
        />
        <span
          style={{
            fontSize: '13px',
            fontWeight: '800px',
            marginBottom: '0rem'
          }}
        >
          {Max_200_characters}
        </span>
        {errors[name] && <Error text={errors[name]} />}
      </Box>
    );
  };

  const renderMultilineText = ({ fields: { label, name, placeholder } }) => {
    return (
      <Box tag="fieldset" between={2}>
        <br />
        <label style={styleTextBold}>{label}</label>
        <textarea
          name={name}
          rows="4"
          cols="50"
          maxlength="500"
          className="frg-input-field color-light"
          placeholder={placeholder}
        />
        <span style={{ fontSize: '13px', fontWeight: '800px' }}>
          {Max_500_characters}
        </span>
        {errors[name] && <Error text={errors[name]} />}
      </Box>
    );
  };

  const renderFile = ({ fields: { label, name, placeholder } }) => {
    return (
      <Box tag="fieldset" between={2} style={styleInputField}>
        <label style={styleTextBold}>{label}</label>
        <label style={{ fontStyle: 'italic', color: 'red' }}>
          {placeholder}
        </label>
        <input
          type="file"
          name={name}
          accept=".png, .jpg, .jpeg , application/pdf ,.doc,.docx,.xlsx, .xls,.csv"
        />
        <label style={{ fontStyle: 'italic', color: 'red' }}>
          {fileUploadError}
        </label>
      </Box>
    );
  };

  const renderFields = fields => {
    return (
      <>
        {fields.map(field => {
          switch (field.fields.type) {
            case InputTypes.RADIO:
              return renderRadio(field);
              break;
            case InputTypes.TEXT:
              return renderText(field);
              break;
            case InputTypes.MULTILINE_TEXT:
              return renderMultilineText(field);
              break;
            case InputTypes.FILE:
              return renderFile(field);
              break;
          }
        })}
        {errors && errors.api && <Error text={errors.api} />}
      </>
    );
  };

  const AUTO_ASSIGN_DEFECTS = (field, formData, profileId) => {
    const webForm = field[0].value;
    const category = field[1].value;
    // Auto-Assign Defects -- SOF-1864
    if (envName === 'dev') {
      //// DEV
      if (
        webForm === 'Wireless' &&
        (category === 'Offers & Pricing' || category === 'Offres & Tarifs')
      ) {
        formData.append('assigned_user_id', 'areeb.hashir@mobilelive.ca'); // Areeb Hashir
        if (profileId !== 269) {
          formData.append('new_update_for_admin', true);
        }
      } else if (
        category === 'User Info' ||
        category === 'informations utilisateur' ||
        category === 'Offers & Pricing' ||
        category === 'Offres & Tarifs'
      ) {
        formData.append('assigned_user_id', 'gohar.aziz@mobilelive.ca'); // Gohar Aziz
        if (profileId !== 147) {
          formData.append('new_update_for_admin', true);
        }
      } else if (
        category === 'Behavioural issue' ||
        category === 'Problème de comportement'
      ) {
        formData.append(
          'assigned_user_id',
          'areeb.hashir+salesrep@mobilelive.ca'
        ); // Areeb Hashir (Admin)
        if (profileId !== 270) {
          formData.append('new_update_for_admin', true);
        }
      }
    } else if (envName === 'qa') {
      //// QA
      if (
        webForm === 'Wireless' &&
        (category === 'Offers & Pricing' || category === 'Offres & Tarifs')
      ) {
        formData.append('assigned_user_id', 'gohar.aziz@mobilelive.ca'); // Gohar Aziz
        if (profileId !== 3883) {
          formData.append('new_update_for_admin', true);
        }
      } else if (
        category === 'User Info' ||
        category === 'informations utilisateur' ||
        category === 'Offers & Pricing' ||
        category === 'Offres & Tarifs'
      ) {
        formData.append(
          'assigned_user_id',
          'muhammad.tauqeer+admin@mobilelive.ca'
        ); // Tauqeer Azhar
        if (profileId !== 6541) {
          formData.append('new_update_for_admin', true);
        }
      } else if (
        category === 'Behavioural issue' ||
        category === 'Problème de comportement'
      ) {
        formData.append('assigned_user_id', 'mubashar.hussain@mobilelive.ca'); // Mubashar Hussain
        if (profileId !== 3508) {
          formData.append('new_update_for_admin', true);
        }
      }
    } else if (envName === 'prod') {
      /// PRODUCTION
      if (
        webForm === 'Wireless' &&
        (category === 'Offers & Pricing' || category === 'Offres & Tarifs')
      ) {
        formData.append('assigned_user_id', 'avani.gautam@telus.com'); // Avani Gautam
        if (profileId !== 6364) {
          formData.append('new_update_for_admin', true);
        }
      } else if (
        category === 'User Info' ||
        category === 'informations utilisateur' ||
        category === 'Offers & Pricing' ||
        category === 'Offres & Tarifs'
      ) {
        formData.append('assigned_user_id', 'tiffany.motayne@telus.com'); // Tiffany Motayne
        if (profileId !== 3431) {
          formData.append('new_update_for_admin', true);
        }
      } else if (
        category === 'Behavioural issue' ||
        category === 'Problème de comportement'
      ) {
        formData.append('assigned_user_id', 'muhammad.tauqeer@mobilelive.ca'); // Muhammad Tauqeer
        if (profileId !== 6386) {
          formData.append('new_update_for_admin', true);
        }
      }
    }
  };

  const onSubmit = e => {
    e.preventDefault();
    const form = e.target;
    const { fields: pageFeilds } = page;
    const fieldValues = pageFeilds.map(
      ({ fields: { name, label, required, type } }) => {
        let { value } = form[name];
        if (type === InputTypes.FILE) {
          value = form[name].files[0] || '';
        }
        const error = (required && !value && ERROR) || undefined;
        return { name, label, value, error };
      }
    );
    const errorFields = fieldValues.filter(f => f.error);
    const errors = (errorFields.length && {}) || null;
    if (errorFields.length) {
      errorFields.forEach(({ error, name }) => {
        errors[name] = error;
      });
    }
    if (errors) {
      scrollToErrors(errors);
      return setErrors(errors);
    }
    const formData = new FormData();
    for (const { name, value } of fieldValues) {
      formData.append(name, value);
    }
    const userData = JSON.parse(localStorage.getItem('userProfile'));
    const { id, name, email } = userData || {};
    formData.append('created_by', id);
    formData.append('name', name);
    formData.append('email', email);
    formData.append('status', STATUS_OPTIONS[0]);
    AUTO_ASSIGN_DEFECTS(fieldValues, formData, id);
    const { DEFECTS_ACCESS_TOKEN, apiHost } = getEnvironment();
    setSubmitting(true);
    axios({
      method: 'post',
      url: `${apiHost}/defects/create`,
      headers: {
        access_token: DEFECTS_ACCESS_TOKEN,
        'Access-Control-Allow-Origin': '*'
      },
      data: formData
    })
      .then(res => {
        if (res && res.status === 200) {
          setFileUploadError('');
          history.push('/report-an-issue/success');
        } else if (res && res.status === 413) {
          setFileUploadError('File Size Limit exceeded (5MB)');
        }
      })
      .catch(() => {
        setErrors({
          api: API_ERROR
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <div style={{ paddingTop: '30px' }}>
        <div className="container" style={{ marginBottom: '4rem' }}>
          <Heading level="h1">{defectManagment}</Heading>
          <br />
          <FormStepContainerBRP>
            <div id="main">
              <form onSubmit={onSubmit}>
                {loader ? (
                  <ValidateLoader message="Loading" />
                ) : (
                  <div className="expand" style={{ marginBottom: '2rem' }}>
                    <ExpandCollapse
                      tag="h1"
                      topDivider={false}
                      open={['collapse']}
                    >
                      <ExpandCollapse.Panel
                        id={'collapse'}
                        header={reportIssueTitle}
                      >
                        {renderFields(page && page.fields)}
                        <div
                          style={{ display: 'flex', justifyContent: 'center' }}
                        >
                          <Spinner
                            spinning={isSubmitting}
                            size="small"
                            label="Request is processing."
                            inline
                          >
                            <Button type="submit">{Button_submit}</Button>
                          </Spinner>
                        </div>
                      </ExpandCollapse.Panel>
                    </ExpandCollapse>
                  </div>
                )}
              </form>
            </div>
          </FormStepContainerBRP>
        </div>
      </div>
      <DefectManagement />
    </>
  );
};

export default ReportAnIssue;
